import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../../../colorVars';
import { addExitTactic } from '../../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/addExitTactic';
import { useWebsocketStore } from '../../../../../../../../../../stores/websocketStore/websocketProvider';
import { CancelOrderButton } from '../../../../../../../../../atomic/buttons/cancelOrderButton/CancelOrderButton';
import IncrementButton from '../../../../../../../../../atomic/buttons/incrementButton/IncrementButton';
import TextButton from '../../../../../../../../../atomic/buttons/textButton/TextButton';
import { EditableTableFieldNoOnBlurUpdate } from '../../../../../../../../../atomic/inputs/editableTableField/EditableFieldNoOnBlurUpdate';
import { Body3, fontSizes } from '../../../../../../../../../atomic/text/Text';
import { underlyingPriceExitValidation } from '../../../../../../../../../forms/tradeplans/AddTradePlan/DataValidation';
import { UnderlyingPriceExitTacticPanelHeader } from '../../../../individualTacticPanels/exits/underlyingPriceExit/UnderlyingPriceExitTacticPanelHeader';
import style from './IndividualAddTacticPanel.module.css';

export const AddUnderlyingPriceExitTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, softOrderId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId];
    const [newPriceInput, setNewPriceInput] = useState(0);
    const [newPctInput, setNewPctInput] = useState(0);
    const [valuePairs, setValuePairs] = useState([]);
    const [headerText, setHeaderText] = useState('Add Break Price');
    const [headerTextColor, setHeaderTextColor] = useState(colors.lightGrey);
    const timeoutRef = useRef(null);
    const details = {
      underlyingPricePairs: valuePairs,
    };

    const addTactic = () => {
      const validationResult = underlyingPriceExitValidation(details);
      if (validationResult[0] === false) {
        setHeaderText(validationResult[1]);
        setHeaderTextColor(colors.red);
        const timer = setTimeout(() => {
          setHeaderText('Add Underlying Price Exit');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      } else {
        addExitTactic(tradePlanId, {
          tactic: 'UNDERLYING_PRICE_EXIT',
          details,
        });
        setHeaderText('Underlying Price Exit Added');
        setHeaderTextColor(colors.green);
        const timer = setTimeout(() => {
          setHeaderText('Add Underlying Price Exit');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      }
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);
    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <UnderlyingPriceExitTacticPanelHeader />
            <tbody>
              {valuePairs.map((pair, index) => {
                return (
                  <tr key={pair.price}>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        {`Price ${index + 1}`}
                      </Body3>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        ${pair.price}
                      </Body3>
                    </td>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        {Number(pair.qtyPct) * 100}%
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <div className={style.inputWrapper}>
                        <CancelOrderButton
                          width={15}
                          clickHandler={() => {
                            const filteredArray = [...valuePairs].filter(
                              (price, i) => i !== index
                            );
                            setValuePairs(filteredArray);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Add
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                    <EditableTableFieldNoOnBlurUpdate
                      inputType="number"
                      fontSize={fontSizes.body3}
                      value={newPriceInput}
                      setValue={(newValue) => setNewPriceInput(newValue)}
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                  </div>
                </td>
                <td className={style.labelTd}>
                  <div className={style.inputWrapper}>
                    <EditableTableFieldNoOnBlurUpdate
                      inputType="number"
                      fontSize={fontSizes.body3}
                      value={newPctInput}
                      setValue={(newValue) => setNewPctInput(newValue)}
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      %
                    </Body3>
                  </div>
                </td>
                <td className={style.labelTd}>
                  <div className={style.inputWrapper}>
                    <IncrementButton
                      color={colors.white}
                      backgroundColor={colors.blue}
                      width={10}
                      stroke={1}
                      handleClick={() => {
                        setValuePairs((prev) => [
                          ...prev,
                          {
                            price: newPriceInput,
                            qtyPct: Number(newPctInput) / 100,
                          },
                        ]);
                      }}
                      paddingPercent={10}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <header className={style.header}>
            <Body3 color={headerTextColor} fontWeight={700}>
              {headerText}
            </Body3>
            <TextButton
              width="20%"
              disabled={tradePlan?.exitTactics['UNDERLYING_PRICE_EXIT'] ? true : false}
              color={colors.lightGrey}
              backgroundColor={
                tradePlan?.exitTactics['UNDERLYING_PRICE_EXIT'] ? colors.grey : colors.blue
              }
              fontSize="small"
              clickHandler={addTactic}
            >
              Add
            </TextButton>
          </header>
        </div>
      </TabPanel>
    );
  }
);

AddUnderlyingPriceExitTacticPanel.tabsRole = 'TabPanel';
